import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ddpsAPI, productsAPI, usersAPI, weighbridgesAPI, clientsAPI } from '../services/api';
import Input from '../components/Input';
import Button from '../components/Button';
import MultiFileUpload from '../components/MultiFileUpload';
import SearchableSelect from '../components/SearchableSelect';
import DDPLineItemsEditor, { createEmptyLineItem } from '../components/DDPLineItemsEditor';

const DDPCreatePage = () => {
  const { t } = useTranslation();
  const { user, getPeseurWeighbridges } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdminOrManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const isPeseur = user?.role === 'PESEUR';
  const [form, setForm] = useState({
    navire: '',
    arrivingDate: '',
    manutentionNumber: '',
    weighbridgeId: '',
    documents: [],
    operatorId: '',
    clientId: '',
    transactionType: 'EXPORT'
  });
  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [weighbridgeOptions, setWeighbridgeOptions] = useState([]);
  const [weighbridgesLoading, setWeighbridgesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    productsAPI.getAll({ limit: 500 })
      .then(res => {
        if (!cancelled && res?.data?.products) {
          setProductOptions(
            res.data.products.map(p => ({ value: p.id, label: p.name || p.id, unit: p.unit || 'ton' }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setProductOptions([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isAdminOrManager) return;
    let cancelled = false;
    setClientsLoading(true);
    usersAPI.getByRole('CLIENT')
      .then(res => {
        if (!cancelled && res?.data?.users) {
          setClientOptions(
            res.data.users.map((u) => {
              const baseLabel =
                u.companyName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email || u.id;
              const clientPhone = u.clientProfile?.contactPhone || u.assignedClient?.contactPhone;
              const phone = String(clientPhone || u.phone || '').trim();
              const label = phone ? `${baseLabel} (${phone})` : baseLabel;
              const searchText = [baseLabel, u.username, u.email, u.phone, clientPhone].filter(Boolean).join(' ');
              return { value: u.id, label, searchText };
            })
          );
        }
      })
      .catch(() => {
        if (!cancelled) setClientOptions([]);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAdminOrManager]);

  useEffect(() => {
    if (!isPeseur) return;
    let cancelled = false;
    setClientsLoading(true);
    clientsAPI.getAll({ page: 1, limit: 500 })
      .then(res => {
        if (!cancelled && res?.data?.clients) {
          setClientOptions(
            res.data.clients.map((c) => {
              const baseLabel = c.companyName || c.contactName || c.id;
              const phone = String(c.contactPhone || '').trim();
              const label = phone ? `${baseLabel} (${phone})` : baseLabel;
              const searchText = [baseLabel, c.contactName, c.contactEmail, c.contactPhone].filter(Boolean).join(' ');
              return { value: c.id, label, searchText };
            })
          );
        }
      })
      .catch(() => {
        if (!cancelled) setClientOptions([]);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPeseur]);

  useEffect(() => {
    let cancelled = false;
    if (isPeseur) {
      const wbs = getPeseurWeighbridges();
      setWeighbridgeOptions(
        wbs.map(wb => ({
          value: wb.id,
          label: `${wb.name || wb.id}${wb.port?.name ? ` - ${wb.port.name}` : ''}`
        }))
      );
      setWeighbridgesLoading(false);
      return () => { cancelled = true; };
    }
    setWeighbridgesLoading(true);
    weighbridgesAPI.getAll()
      .then(res => {
        if (!cancelled && res?.data?.weighbridges) {
          setWeighbridgeOptions(
            res.data.weighbridges
              .filter((wb) => wb.status === 'active')
              .map((wb) => ({
                value: wb.id,
                label: `${wb.name || wb.id}${wb.port?.name ? ` - ${wb.port.name}` : ''}`
              }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setWeighbridgeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setWeighbridgesLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPeseur, getPeseurWeighbridges]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (isAdminOrManager && !form.operatorId) {
      nextErrors.operatorId = t('ddp.selectClient');
    }
    if (isPeseur && !form.clientId) {
      nextErrors.clientId = t('ddp.selectPayingClient');
    }
    if (isPeseur && !form.weighbridgeId) {
      nextErrors.weighbridgeId = t('weighing.quickClientWeighbridgeRequired');
    }
    if (!form.navire.trim()) nextErrors.navire = t('ddp.navire');
    if (!form.arrivingDate) nextErrors.arrivingDate = t('ddp.arrivingDate');
    if (!form.manutentionNumber.trim()) nextErrors.manutentionNumber = t('ddp.manutentionNumber');
    if (!form.transactionType) nextErrors.transactionType = t('ddp.transactionType');

    const hasValidLine = lineItems.some((item) => item.productId && String(item.totalWeight).trim() !== '');
    if (!hasValidLine) {
      nextErrors.lineItems = t('ddp.lineItemsRequired');
    }

    lineItems.forEach((item, index) => {
      if (!item.productId) {
        nextErrors[`lineItems.${index}.productId`] = t('ddp.selectProduct');
      }
      const totalWeight = Number(item.totalWeight);
      if (!String(item.totalWeight).trim() || !Number.isFinite(totalWeight) || totalWeight <= 0) {
        nextErrors[`lineItems.${index}.totalWeight`] = t('ddp.validTotalWeight');
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('navire', form.navire);
      formData.append('arrivingDate', form.arrivingDate);
      formData.append('manutentionNumber', form.manutentionNumber);
      formData.append('transactionType', form.transactionType);
      formData.append('lineItems', JSON.stringify(
        lineItems.map((item) => ({
          productId: item.productId,
          totalWeight: Number(item.totalWeight)
        }))
      ));
      if (form.weighbridgeId) {
        formData.append('weighbridgeId', form.weighbridgeId);
      }
      
      if (isAdminOrManager && form.operatorId) {
        formData.append('operatorId', form.operatorId);
      }
      if (isPeseur && form.clientId) {
        formData.append('clientId', form.clientId);
      }
      
      // Add files and descriptions
      const descriptions = [];
      form.documents.forEach((doc) => {
        if (doc.file) {
          formData.append('documents', doc.file);
          descriptions.push(doc.description || '');
        }
      });
      
      if (descriptions.length > 0) {
        formData.append('documentDescriptions', JSON.stringify(descriptions));
      }
      
      await ddpsAPI.create(formData);
      setSuccess(true);
      setErrors({});
      toast.success(t('ddp.success'));
      navigate('/ddp/list');
    } catch (err) {
      const d = err?.response?.data;
      const general =
        d?.code === 'DOCUMENT_STORAGE_LIMIT'
          ? t('ddp.storageLimitExceeded', { limitMb: d.limitMb ?? '—' })
          : d?.error || err?.message || 'Error';
      setErrors((prev) => ({
        ...prev,
        general,
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto mt-8">
      <h2 className="text-xl font-bold mb-4">{t('ddp.createTitle')}</h2>
      {success ? <div className="text-green-600">{t('ddp.success')}</div> : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.general && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errors.general}
            </div>
          )}
          {isPeseur && (
            <SearchableSelect
              label={t('ddp.payingClient')}
              name="clientId"
              value={form.clientId}
              onChange={handleChange}
              options={clientOptions}
              placeholder={t('ddp.selectPayingClient')}
              required
              disabled={clientsLoading}
              error={errors.clientId}
            />
          )}
          {isAdminOrManager && (
            <SearchableSelect
              label={t('ddp.clientOnBehalf')}
              name="operatorId"
              value={form.operatorId}
              onChange={handleChange}
              options={clientOptions}
              placeholder={t('ddp.selectClient')}
              required
              disabled={clientsLoading}
              error={errors.operatorId}
            />
          )}
          <Input label={t('ddp.navire')} name="navire" value={form.navire} onChange={handleChange} required error={errors.navire} />
          <Input label={t('ddp.manutentionNumber')} name="manutentionNumber" value={form.manutentionNumber} onChange={handleChange} required error={errors.manutentionNumber} />
          <SearchableSelect
            label={t('users.selectWeighbridge')}
            name="weighbridgeId"
            value={form.weighbridgeId}
            onChange={handleChange}
            options={weighbridgeOptions}
            placeholder={t('users.selectWeighbridge')}
            disabled={weighbridgesLoading}
            required={isPeseur}
            error={errors.weighbridgeId}
          />
          <Input label={t('ddp.arrivingDate')} name="arrivingDate" type="date" value={form.arrivingDate} onChange={handleChange} required error={errors.arrivingDate} />
          <SearchableSelect
            label={t('ddp.transactionType')}
            name="transactionType"
            value={form.transactionType}
            onChange={handleChange}
            options={[
              { value: 'EXPORT', label: t('loads.transactionTypes.export') },
              { value: 'IMPORT', label: t('loads.transactionTypes.import') }
            ]}
            placeholder={t('ddp.selectTransactionType')}
            required
            error={errors.transactionType}
          />
          <DDPLineItemsEditor
            t={t}
            lineItems={lineItems}
            setLineItems={setLineItems}
            productOptions={productOptions}
            productsLoading={productsLoading}
            errors={errors}
          />
          <MultiFileUpload 
            label={t('ddp.documents')} 
            name="documents" 
            value={form.documents}
            onChange={documents => setForm(f => ({ ...f, documents }))} 
          />
          <Button type="submit" loading={loading}>{t('common.submit')}</Button>
        </form>
      )}
    </div>
  );
};

export default DDPCreatePage;
